"use client";

import { useSession } from "@golf-district/auth/nextjs-exports";
import type { BookingGroup, CombinedObject, PriceClassEntry } from "@golf-district/shared";
import { useAppContext } from "~/contexts/AppContext";
import { useChannel } from "~/contexts/ChannelContext";
import { useCourseContext } from "~/contexts/CourseContext";
import { useUserContext } from "~/contexts/UserContext";
import { api } from "~/utils/api";
import { formatMoney, formatTime, getTime } from "~/utils/formatters";
import { googleAnalyticsEvent } from "~/utils/googleAnalyticsUtils";
import { microsoftClarityEvent } from "~/utils/microsoftClarityUtils";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useCopyToClipboard } from "usehooks-ts";
import { useAddToWatchlist } from "~/hooks/useAddToWatchlist";
import { useCheckListingAvailability } from "~/hooks/useCheckListingAvailability";
import { Avatar } from "../avatar";
import { FilledButton } from "../buttons/filled-button";
import { OutlineButton } from "../buttons/outline-button";
import { Heart } from "../icons/heart";
import { Hidden } from "../icons/hidden";
import { InlineAlert } from "../inline-alert/inline-alert";
import { ChoosePlayers } from "../input/choose-players";
import { ChooseHoles } from "../input/choose-holes";
import { Spinner } from "../loading/spinner";
import { ManageTeeTimeListing } from "../my-tee-box-page/manage-tee-time-listing";
import { Tooltip } from "../tooltip";
import { MakeAnOffer } from "../watchlist-page/make-an-offer";
import { Info } from "../icons/info";
import { GolfDistrictLight } from "../icons/golf-district-light";

const PlayersOptions = ["1", "2", "3", "4"];

export const TeeTimeV2 = ({
    time,
    items,
    canChoosePlayer,
    players,
    price,
    soldById,
    soldByImage,
    soldByName,
    availableSlots,
    teeTimeId,
    isLiked,
    status = "FIRST_HAND",
    minimumOfferPrice,
    bookingIds,
    listingId,
    firstHandPurchasePrice,
    className,
    showFullDate,
    children,
    listedSlots,
    handleLoading,
    refetch,
    groupId,
    allowSplit,
    hasPriceClass,
    priceClasses,
    defaultPriceClassId,
    defaultHole,
    availableHoles
}: {
    time: string;
    items: CombinedObject | BookingGroup;
    index: number;
    canChoosePlayer: boolean;
    players: string;
    price: number;
    isOwned: boolean;
    soldById: string;
    soldByImage: string;
    soldByName: string;
    availableSlots: number;
    teeTimeId: string;
    isLiked: boolean;
    status: "UNLISTED" | "FIRST_HAND" | "SECOND_HAND" | "OFFER";
    minimumOfferPrice: number | undefined;
    bookingIds: string[];
    listingId: string | undefined;
    firstHandPurchasePrice: number | undefined;
    className?: string;
    showFullDate?: boolean;
    children?: ReactNode;
    listedSlots?: number | null;
    handleLoading?: (val: boolean) => void;
    refetch?: () => Promise<unknown>;
    groupId?: string;
    allowSplit: boolean;
    hasPriceClass?: boolean;
    priceClasses?: PriceClassEntry[];
    defaultPriceClassId?: string;
    defaultHole?: number;
    availableHoles?: (9 | 18)[];
}) => {
    const isOfferCard = status === "OFFER";
    const [, copy] = useCopyToClipboard();
    const { entity } = useAppContext();
    const [isCopied, setIsCopied] = useState<boolean>(false);
    const { course, getAllowedPlayersForTeeTime } = useCourseContext();
    const courseId = course?.id;

    const [selectedPriceClassId, setSelectedPriceClassId] = useState<string>(defaultPriceClassId ?? "");
    const [selectedHoles, setSelectedHoles] = useState<number>(defaultHole ?? 18);
    const [selectedPlayers, setSelectedPlayers] = useState<string>("");

    const activePriceClass = priceClasses?.find(
        (pc) => pc.id === selectedPriceClassId && pc.holes === selectedHoles
    );
    const resolvedTeeTimeId = activePriceClass?.teeTimeId ?? teeTimeId;

    const courseLevelAllowedPlayers = useMemo(
        () => getAllowedPlayersForTeeTime(items.time, items.date, availableSlots),
        [getAllowedPlayersForTeeTime, items.time, items.date, availableSlots]
    );

    const usePriceClassPolicy =
        (status === "UNLISTED" || status === "FIRST_HAND") &&
        !!hasPriceClass &&
        activePriceClass?.salePolicyMaxPlayers != null;

    const policyMaxPlayers = usePriceClassPolicy
        ? Math.min(activePriceClass.salePolicyMaxPlayers!, 4)
        : null;

    const { allowedPlayers, effectiveAvailableSlots } = useMemo(() => {
        if (usePriceClassPolicy && policyMaxPlayers != null) {
            return {
                allowedPlayers: {
                    numberOfPlayers: PlayersOptions.slice(0, policyMaxPlayers),
                    selectStatus: "" as const,
                },
                effectiveAvailableSlots: Math.min(availableSlots, policyMaxPlayers),
            };
        }
        return {
            allowedPlayers: courseLevelAllowedPlayers,
            effectiveAvailableSlots: availableSlots,
        };
    }, [
        usePriceClassPolicy,
        policyMaxPlayers,
        courseLevelAllowedPlayers,
        availableSlots,
    ]);

    const activePrice = activePriceClass?.pricePerGolfer ?? price;

    const numberOfPlayers = allowedPlayers?.numberOfPlayers;
    useEffect(() => {
        if (isOfferCard) {
            setSelectedPlayers(availableSlots.toString());
            return;
        }
        if (numberOfPlayers?.length !== 0 && numberOfPlayers?.[0] && (status === "UNLISTED" || status === "FIRST_HAND")) {
            setSelectedPlayers(String(numberOfPlayers[0]));
        } else if (allowedPlayers?.selectStatus === "ALL_PLAYERS" && (status === "UNLISTED" || status === "FIRST_HAND")) {
            setSelectedPlayers(String(availableSlots));
        } else {
            setSelectedPlayers(
                status === "UNLISTED" || status === "FIRST_HAND" ? "1" : listedSlots?.toString() ?? players
            );
        }
    }, [
        allowedPlayers,
        courseId,
        items.time,
        items.date,
        status,
        availableSlots,
        players,
    ]);

    useEffect(() => {
        if (isOfferCard || status === "SECOND_HAND" || policyMaxPlayers == null) return;
        const current = parseInt(selectedPlayers, 10) || 1;
        if (current > policyMaxPlayers) {
            setSelectedPlayers(String(policyMaxPlayers));
        }
    }, [selectedPriceClassId, selectedHoles, policyMaxPlayers, isOfferCard, status]);

    const timezoneCorrection = course?.timezoneCorrection;
    const [isMakeAnOfferOpen, setIsMakeAnOfferOpen] = useState<boolean>(false);
    const { data: session } = useSession();
    const [isManageOpen, setIsManageOpen] = useState<boolean>(false);
    const { user } = useUserContext();
    const router = useRouter();
    const { channel } = useChannel();
    const utils = api.useContext();
    const auditLog = api.webhooks.auditLog.useMutation();
    const { checkListingAvailability, availabilityError } = useCheckListingAvailability(listingId ?? "");
    const groupBookingParams = useMemo(() => {
        return `date=${items.date?.split("T")[0]}&time=${items.time}`
    }, [items]);

    const shouldShowGroupBookingButton = useMemo(() => {
        if (isOfferCard) return false;
        if (course?.groupStartTime && course?.groupEndTime && items.time) {
            return (items.time >= course?.groupStartTime && items.time <= course?.groupEndTime) ? true : false
        } else {
            return true
        }
    }, [items]);

    const logAudit = async () => {
        await auditLog.mutateAsync({
            userId: user?.id ?? "",
            teeTimeId: teeTimeId ?? "",
            bookingId: "",
            listingId: listingId ?? "",
            courseId,
            eventId: "TEE_TIME_IN_CART",
            json: `TEE_TIME_IN_CART `,
        });
    };

    useEffect(() => {
        (ref: HTMLSpanElement | null) => {
            if (ref) {
                return ref.scrollWidth > ref.clientWidth;
            }
            return false;
        };
    }, [items]);

    useEffect(() => {
        if (isMakeAnOfferOpen || isManageOpen) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }
    }, [isMakeAnOfferOpen, isManageOpen]);
    const { setPrevPath } = useAppContext();

    const { addToWatchlist: addToWatchlistShared, watchlistError } = useAddToWatchlist();
    const [optimisticLike, setOptimisticLike] = useState(isLiked);
    // const { refetch: refetchCheckTeeTime } =
    //   api.teeBox.checkIfTeeTimeStillListed.useQuery(
    //     {
    //       bookingId: bookingIds[0] || "",
    //     },
    //     {
    //       enabled: false,
    //     }
    //   );

    const addToWatchlist = async () => {
        googleAnalyticsEvent({
            action: "fav_ttlist",
            params: { item_id: teeTimeId, booking_type: "Traditional" },
        });
        await addToWatchlistShared(teeTimeId, () => setOptimisticLike(!optimisticLike));
    };
    const fullUrl = window.location.href;
    const url = new URL(fullUrl);
    const pathname = url.pathname;

    const buyTeeTime = async () => {
        // Show the full-screen loader immediately on click, before any awaits,
        // so there's no dead time before the user sees feedback.
        if (handleLoading) {
            handleLoading(true);
        }

        // const isTeeTimeAvailable = await refetchCheckTeeTime();
        // if (!isTeeTimeAvailable.data && status === "SECOND_HAND") {
        //   toast.error("Oops! Tee time is not available anymore");
        //   return;
        // }
        await logAudit();
        microsoftClarityEvent({
            action: `CLICKED ON BUY`,
            category: "BUY TEE TIME",
            label: "user clicked on buy to purchase tee time",
            value: pathname,
        });
        googleAnalyticsEvent({
            action: "btn_ttlist_buy",
            category: "TEE TIME ",
            label: "user clicked on buy to purchase tee time",
            params: {
                item_id: teeTimeId,
                item_category: "tee_time",
                booking_type: "Traditional",
                quantity: Number(selectedPlayers) || 1,
                price_class_id: selectedPriceClassId,
                price_class_name: activePriceClass?.name,
                holes: selectedHoles,
            },
        });

        if (!user || !session) {
            if (status === "FIRST_HAND") {
                setPrevPath({
                    path: `/${course?.id}/checkout?teeTimeId=${resolvedTeeTimeId}&playerCount=${selectedPlayers}&selectedPriceClassId=${selectedPriceClassId}&selectedHoles=${selectedHoles}`,
                    createdAt: new Date().toISOString(),
                });
            }
            if (status === "SECOND_HAND") {
                setPrevPath({
                    path: `/${course?.id}/checkout?listingId=${listingId}&playerCount=${selectedPlayers}`,
                    createdAt: new Date().toISOString(),
                });
            }
            if (status === "OFFER") {
                setPrevPath({
                    path: `/${course?.id}/checkout-offer?teeTimeId=${teeTimeId}&bookingId=${bookingIds[0]}&playerCount=${selectedPlayers}&holes=${selectedHoles}`,
                    createdAt: new Date().toISOString(),
                });
            }
            void router.push(`/${course?.id}/login`);
            return;
        }
        // Everything else the checkout page fetches on mount that isn't specific to
        // FIRST_HAND vs SECOND_HAND — warm it here too so checkout mounts fully loaded.
        // (checkMaxReservationsAndMaxRounds isn't included: its `roundsToBook` input
        // depends on checkout-page-only state that isn't resolved yet at click time,
        // so a prefetched copy wouldn't match its cache key anyway.)
        const prefetchSharedCheckoutData = () =>
            Promise.all([
                utils.user.getCountryCode.fetch({}),
                utils.course.getPhoneNumberMandatoryAtCheckout.fetch({ courseId: courseId ?? "" }),
                utils.checkout.isAppleEnabledReloadWidget.fetch({}),
                utils.course.getPrivacyPolicyAndTCByCourse.fetch({ courseId: courseId ?? "" }),
                utils.systemNotification.getSystemNotification.fetch({}),
                utils.systemNotification.getCourseGlobalNotification.fetch({ courseId: courseId ?? "" }),
                utils.playBuddy.getPlayBuddies.fetch({ userId: user.id, allowPagination: false }),
            ]).catch(() => undefined);

        if (status === "FIRST_HAND") {
            // Fetch the checkout page's tee-time data here, on the listing page, while
            // the full-screen loader is up — the checkout page's own getTeeTimeById
            // query then hits a warm cache on mount instead of fetching (and spinning) again.
            await Promise.all([
                utils.searchRouter.getTeeTimeById
                    .fetch({
                        teeTimeId: resolvedTeeTimeId,
                        holes: selectedHoles,
                        channelId: channel?.id,
                        courseId,
                    })
                    .catch(() => undefined),
                prefetchSharedCheckoutData(),
            ]);
            void router.push(
                `/${course?.id}/checkout?teeTimeId=${resolvedTeeTimeId}&playerCount=${selectedPlayers}&selectedPriceClassId=${selectedPriceClassId}&selectedHoles=${selectedHoles}`
            );
        }
        if (status === "SECOND_HAND") {
            const canProceed = await checkListingAvailability(allowSplit, user.id);
            if (!canProceed) {
                if (handleLoading) {
                    handleLoading(false);
                }
                return;
            }
            await Promise.all([
                utils.searchRouter.getListingById
                    .fetch({ listingId: listingId! })
                    .catch(() => undefined),
                prefetchSharedCheckoutData(),
            ]);
            void router.push(
                `/${course?.id}/checkout?listingId=${listingId}&playerCount=${selectedPlayers}&selectedHoles=${selectedHoles}`
            );
        }
        if (status === "OFFER") {
            void router.push(
                `/${course?.id}/checkout-offer?teeTimeId=${teeTimeId}&bookingId=${bookingIds[0]}&playerCount=${selectedPlayers}&holes=${selectedHoles}`
            );
        }
    };

    const makeAnOffer = () => {
        googleAnalyticsEvent({
            action: "btn_ttlist_make_offer",
            params: { item_id: teeTimeId },
        });
        setIsMakeAnOfferOpen(true);
    };

    const href = useMemo(() => {
        const player = selectedPlayers === "0" ? 1 : selectedPlayers;
        const routes = {
            OFFER: `/${course?.id}/checkout-offer?teeTimeId=${teeTimeId}&bookingId=${bookingIds[0]}&playerCount=${players}&holes=${selectedHoles}`,
            FIRST_HAND: `/${course?.id}/checkout?teeTimeId=${resolvedTeeTimeId}&playerCount=${player}&selectedPriceClassId=${selectedPriceClassId}&selectedHoles=${selectedHoles}`,
            SECOND_HAND: `/${course?.id}/checkout?listingId=${listingId}&playerCount=${player}&selectedHoles=${selectedHoles}`,
        };
        return routes[status as "OFFER" | "FIRST_HAND" | "SECOND_HAND"] ?? `/${courseId}`;
    }, [status, listingId, resolvedTeeTimeId, courseId, soldById, selectedPlayers, selectedPriceClassId, selectedHoles]);

    const isSuggested = status === "UNLISTED";

    const share = async () => {
        googleAnalyticsEvent({
            action: "share_ttlist",
            params: { item_id: teeTimeId },
        });
        const currentUrl = window.location.host;
        if (navigator?.share) {
            await navigator.share({ url: `${href}` });
        } else {
            await copyToClipboard(`${currentUrl}${href}`);
        }
    };

    const copyToClipboard = async (url: string) => {
        await copy(url);
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
        }, 1000);
    };

    useEffect(() => {
        // if (status === "SECOND_HAND") {
        //   setSelectedPlayers(isOwned ? players : availableSlots.toString());
        // }
    }, [status, availableSlots]);

    const openManage = () => {
        if (status === "UNLISTED") {
            router.push(`/${courseId}/my-tee-box`);
            return;
        }
        router.push(`/${courseId}/my-tee-box?section=my-listed-tee-times&listId=${listingId}`);
        // setIsManageOpen(true);
    };

    return (
        <>
            {children}
            <div
                data-testid="tee-time-id"
                data-test={
                    status === "SECOND_HAND" ? "secondary_listed" : "primary_listed"
                }
                className={`md:rounded-xl rounded-lg ${isOfferCard ? "bg-[#ececec]" : "bg-secondary-white"
                    } w-fit min-w-[14.375rem] md:min-w-[16.5625rem] w-full ${className ?? ""}`}
            >
                <div className="border-b border-stroke">
                    <div className="flex justify-between py-1 px-2 md:px-3 md:p-3 items-center">
                        <div className="flex flex-col">
                            <div className="font-semibold text-base md:text-xl unmask-time">
                                {showFullDate
                                    ? formatTime(time, true, timezoneCorrection)
                                    : getTime(time, timezoneCorrection)}
                            </div>
                            {course?.name && (
                                <div className="text-[0.625rem] md:text-[0.75rem] text-primary-gray leading-tight">
                                    {course.name}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {status === "UNLISTED" && (
                                <Hidden className="w-[0.75rem] md:w-[1.25rem]" />
                            )}
                            {/* {(status === "OFFER") && (
                                <GolfDistrictLight className="w-[2.5rem] h-[2.5rem] mr-2" />
                            )} */}
                            {status !== "OFFER" && status !== "SECOND_HAND" && (
                                <Tooltip
                                    trigger={
                                        <Avatar
                                            src={soldByImage}
                                            className="!min-h-[2.5rem] !min-w-[5rem] max-h-[2.5rem] max-w-[5rem] h-[2.5rem] w-[5rem] md:min-h-[2.5rem] md:min-w-[5rem] md:max-h-[2.5rem] md:max-w-[5rem] md:h-[2.5rem] md:w-[5rem] lg:w-[5rem] lg:h-[2.5rem]"
                                            isRounded={false}
                                        />
                                    }
                                    content={"Sold by " + soldByName}
                                />
                            )}
                            {status === "SECOND_HAND" && (
                                <Tooltip
                                    trigger={<Spinner className="w-[2.5rem] h-[2.5rem]" />}
                                    content="Sold by another Golf District golfer."
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-1 md:gap-4 p-2 md:p-3 text-[0.625rem] md:text-[0.875rem]">

                    <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-1 relative pt-1.5 md:pt-0">
                            {isSuggested && !isOfferCard ? (
                                <div className="absolute -top-[.18rem] md:-top-3.5 text-[0.5625rem] md:text-[0.75rem] text-primary-gray">
                                    Suggested
                                </div>
                            ) : null}
                            <div className="flex items-center">
                                <div className={`${isOfferCard ? "text-sm mb-3" : "text-sm md:text-base"} font-semibold text-secondary-black`}>
                                    {!isOfferCard ? formatMoney(activePrice) : "Currently Owned"}
                                </div>
                                {!isOfferCard && (
                                    <div className="text-[0.75rem] md:text-[0.875rem] text-primary-gray">
                                        /golfer
                                    </div>
                                )}
                                {/* {isOfferCard && (
                                    <Tooltip
                                        trigger={
                                            <Info className="ml-1 mb-4 h-4 w-4 text-primary-gray hover:text-secondary-black transition-colors" />
                                        }
                                        content="This tee time isn't listed for sale. Submit an offer to buy that may entice the owner of the tee time to sell!"
                                    />
                                )} */}
                            </div>
                        </div>
                        <div className="flex md:min-h-[1.9375rem] items-center gap-2">
                            {canChoosePlayer ? (
                                <ChoosePlayers
                                    id="choose-players"
                                    players={selectedPlayers}
                                    setPlayers={setSelectedPlayers}
                                    playersOptions={PlayersOptions}
                                    availableSlots={
                                        status === "SECOND_HAND" ? listedSlots || 0 : effectiveAvailableSlots
                                    }
                                    isDisabled={
                                        (status === "SECOND_HAND" && !allowSplit) ||
                                        allowedPlayers?.selectStatus === "ALL_PLAYERS"
                                    }
                                    className="md:px-[1rem] md:py-[.25rem] md:!text-[0.875rem] !text-[0.625rem] py-[.1rem]"
                                    teeTimeId={teeTimeId}
                                    numberOfPlayers={numberOfPlayers ? (
                                        !(status === "SECOND_HAND") ? numberOfPlayers : PlayersOptions.filter(player => player <= (listedSlots?.toString() ?? "0"))
                                    ) : []}
                                    status={status}
                                    supportsGroupBooking={shouldShowGroupBookingButton ? course?.supportsGroupBooking : false}
                                    allowSplit={allowSplit}
                                    groupBookingParams={groupBookingParams}
                                    isOfferCard={isOfferCard}
                                />
                            ) : (
                                players && (
                                    <div className="text-sm">
                                        {players} golfer{parseInt(players) > 1 ? "s" : ""}
                                    </div>
                                )
                            )}
                        </div>
                    </div>

                    {/* Holes toggle + Price class dropdown row */}
                    {((!isOfferCard || !listingId) && hasPriceClass && priceClasses && priceClasses?.length > 0) && (
                        <div className="flex items-center gap-2 py-2">
                            {/* Holes Toggle or Static Badge */}
                            {availableHoles?.length === 1 ? (
                                <span className="border rounded-full px-6 h-[34px] bg-primary text-sm font-medium text-white flex items-center whitespace-nowrap">
                                    {availableHoles[0]} holes
                                </span>
                            ) : (
                                <ChooseHoles
                                    holes={selectedHoles as 9 | 18}
                                    setHoles={(val) => {
                                        setSelectedHoles(val);
                                        const defaultForHoles =
                                            priceClasses?.find((pc) => pc.isPublic && pc.holes === val) ??
                                            priceClasses?.find((pc) => pc.holes === val);
                                        setSelectedPriceClassId(defaultForHoles?.id ?? "");
                                    }}
                                    isDisabled={isOfferCard || status === "SECOND_HAND"}
                                    availableHoles={availableHoles}
                                    className="!text-xs"
                                />
                            )}
                            {/* Price Class Select */}
                            <div className="relative flex-1">
                                <select
                                    value={selectedPriceClassId}
                                    onChange={(e) => {
                                        const selectedName = priceClasses?.find(
                                            (pc) => pc.id === e.target.value
                                        )?.name;
                                        setSelectedPriceClassId(e.target.value);
                                        googleAnalyticsEvent({
                                            action: "dd_ttlist_price_class",
                                            params: {
                                                item_id: teeTimeId,
                                                price_class_id: e.target.value,
                                                price_class_name: selectedName,
                                                selected_value: selectedName,
                                                holes: selectedHoles,
                                            },
                                        });
                                    }}
                                    className="appearance-none border border-gray-300 rounded-full pl-4 pr-6 bg-white text-sm font-medium text-gray-700 
                                    cursor-pointer focus:outline-none w-full truncate h-[34px]"
                                >
                                    {priceClasses
                                        .filter((priceClass) => priceClass.holes === selectedHoles)
                                        .map((priceClass) => (
                                            <option key={priceClass.id} value={priceClass.id}>
                                                {priceClass.name}
                                            </option>
                                        ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-6">

                        <div className="flex items-center gap-1">
                            {(course?.supportsWatchlist || isOfferCard) ? (
                                <OutlineButton
                                    className={`md:px-[.5rem] px-[0.375rem] py-[0.375rem] md:py-2 ${isOfferCard ? "!border-[#5200BE]" : ""
                                        }`}
                                    onClick={addToWatchlist}
                                    data-testid="watch-list-id"
                                    data-test={teeTimeId}
                                    data-qa={optimisticLike}
                                >
                                    <Heart
                                        className="w-[0.8125rem] md:w-[1.125rem]"
                                        fill={optimisticLike && isOfferCard ? "#5200BE" : optimisticLike ? "var(--primary-color)" : "none"}
                                        stroke={isOfferCard ? "#5200BE" : "var(--primary-color)"}
                                    />
                                </OutlineButton>
                            ) : null}

                            {/* <Link
                                href={href}
                                data-testid="details-button-id"
                                data-test={teeTimeId}
                                data-qa={"Details"}
                                data-cy={time}
                            >
                                <OutlineButton className="!py-[.28rem] md:py-1.5">
                                    Details
                                </OutlineButton>
                            </Link> */}
                            {<OutlineButton
                                onClick={() => void share()}
                                className={`w-full whitespace-nowrap ${isOfferCard ? "!border-[#5200be]" : ""}`}
                                data-testid="share-button-id"
                            >
                                <div className={`flex items-center justify-center gap-2 ${isOfferCard ? "!text-[#5200be]" : ""}`}>
                                    {isCopied ? <>Copied</> : <>Share</>}
                                </div>
                            </OutlineButton>}
                        </div>
                        {soldById === user?.id && session ? (
                            <FilledButton
                                onClick={openManage}
                                className="whitespace-nowrap"
                                data-testid="sell-button-id"
                                data-test={teeTimeId}
                                data-qa="Buy"
                                data-cy={time}
                            >
                                {status === "UNLISTED" ? "Sell" : "Manage"}
                            </FilledButton>
                        ) : (
                            <>
                                {isOfferCard ? (
                                    <FilledButton
                                        className="whitespace-nowrap !min-w-[5.125rem] w-full md:min-w-[6.875rem] !py-[.28rem] md:py-1.5 !border-[#5200be] !bg-[#5200be]"
                                        onClick={buyTeeTime}
                                        data-testid="make-an-offer-id"
                                        data-test={teeTimeId}
                                        data-qa="Make an Offer"
                                        data-cy={time}
                                    >
                                        Make an Offer
                                    </FilledButton>
                                ) : isSuggested ? (
                                    <FilledButton
                                        className="whitespace-nowrap !px-3 !min-w-[5.125rem] md:min-w-[6.875rem]"
                                        onClick={makeAnOffer}
                                        data-testid="make-an-offer-id"
                                        data-test={teeTimeId}
                                        data-qa="Make an Offer"
                                        data-cy={time}
                                    >
                                        Make an Offer
                                    </FilledButton>
                                ) : (
                                    <FilledButton
                                        className="whitespace-nowrap !min-w-[5.125rem] md:min-w-[6.875rem] !py-[.28rem] md:py-1.5 w-full"
                                        onClick={buyTeeTime}
                                        data-testid="buy-tee-time-id"
                                        data-test={teeTimeId}
                                        data-qa="Buy"
                                        data-cy={time}
                                        id="buy-button"
                                    >
                                        Buy
                                    </FilledButton>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {watchlistError && <InlineAlert status="error">{watchlistError}</InlineAlert>}
                {availabilityError && <InlineAlert status="error">{availabilityError}</InlineAlert>}
                {isMakeAnOfferOpen && (
                    <MakeAnOffer
                        isMakeAnOfferOpen={isMakeAnOfferOpen}
                        setIsMakeAnOfferOpen={setIsMakeAnOfferOpen}
                        availableSlots={availableSlots}
                        courseName={course?.name ?? ""}
                        courseImage={course?.logo ?? ""}
                        date={time}
                        minimumOfferPrice={
                            minimumOfferPrice ?? firstHandPurchasePrice ?? price
                        }
                        bookingIds={bookingIds ?? []}
                    />
                )}
                {isManageOpen && (
                    <ManageTeeTimeListing
                        isManageTeeTimeListingOpen={isManageOpen}
                        setIsManageTeeTimeListingOpen={setIsManageOpen}
                        selectedTeeTime={{
                            listingId: listingId ?? "",
                            courseName: course?.name ?? "",
                            courseLogo: course?.logo ?? "",
                            courseId: courseId ?? "",
                            date: time,
                            firstHandPrice: firstHandPurchasePrice ?? 0,
                            miniumOfferPrice: minimumOfferPrice ?? 0,
                            listPrice: price,
                            status: status,
                            listedSpots: Array.from({ length: availableSlots }).fill(
                                "golfer"
                            ) as string[],
                            teeTimeId: teeTimeId,
                            listedSlotsCount: listedSlots ?? 1,
                            groupId: groupId ?? "",
                            totalMerchandiseAmount: 0,
                            playerCountInGroup: 0
                        }}
                        refetch={refetch}
                    />
                )}
            </div>
        </>
    );
};
